import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import NavigationService from "./utils/navigationService";
import Login from "./pages/auth/login";
import SignUp from "./pages/auth/signup";
import OTPVerification from "./pages/auth/otpVerification";
import "./App.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ForgotPassword from "./pages/auth/forgotPassword";
import Home from "./pages/home";
import ResetPassword from "./pages/auth/resetPassword";
import BrandLogo from "./components/BrandLogo";
import { useDispatch, useSelector } from "react-redux";
import { getToken, getUserId } from "./constants/asyncStoreManager";
import { setIsLoggedIn } from "./store/slices/authState";
import { appApiHandler } from "./api/appApiHandler";
import { setUserData } from "./store/slices/userState";
import { setCategories } from "./store/slices/categoryState";
import SearchPage from "./pages/search";
import CategoryProduct from "./pages/categoryProduct";
import ProductDetails from "./pages/productDetails";
import Profile from "./pages/profile";
import ChangePassword from "./pages/changePassword";
import BankDetails from "./pages/bankDetails";
import Whishlist from "./pages/whishlist";
import Cart from "./pages/cart";
import Address from "./pages/address";
import AddAddress from "./pages/address/addAddress";
import Checkout from "./pages/checkout";
import Orders from "./pages/order";
import OrderDetails from "./pages/order/orderDetails";
import RatingAndReview from "./pages/order/ratingAndReview";
import { setCartCount, setWhishlistCount } from "./store/slices/countState";
import { setWhishlist } from "./store/slices/whishlistState";
import { setHomePromoData } from "./store/slices/homeState";
import Notifications from "./pages/notification";
import SupportTicket from "./pages/supportTicket";
import CreateSupportTicket from "./pages/supportTicket/createSupportTicket";
import SupportChat from "./pages/supportTicket/supportChat";
import PrivacyPolicy from "./pages/privacyPolicy";
import TermsAndCondition from "./pages/termsAndCondition";
import ReturnPolicy from "./pages/returnPolicy";
import ContactUs from "./pages/contactUs";
import ShippingPolicy from "./pages/shippingPolicy";
import TopBar from "./pages/home/components/TopBar";
import HomeHeader from "./components/header/HomeHeader";
import AppPrivacyPolicy from "./pages/AppPrivacyPolicy";

const AppContent = () => {
  const location = useLocation();

  const hideHeader = location.pathname === "/mobile-privacy-policy";

  return (
    <>
      {!hideHeader && (
        <>
          <TopBar />
          <HomeHeader />
        </>
      )}

      <AppRoutes />
    </>
  );
};

const Splash = () => (
  <div className="splash">
    <BrandLogo alt="Yug Mantra" />
  </div>
);

const AppRoutes = () => {
  const { isLoggedIn } = useSelector((state) => state.authState);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  // Set up navigation service
  useEffect(() => {
    NavigationService.setTopLevelNavigator(navigate);
  }, [navigate]);

  // Show loading or wait for auth to settle
  if (isLoading) {
    return null; // Show nothing while auth is loading
  }

  return (
    <Routes>
      <>
        <Route path="/" element={<Home />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/category" element={<CategoryProduct />} />
        <Route path="/product" element={<ProductDetails />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/bank-details" element={<BankDetails />} />
        <Route path="/whishlist" element={<Whishlist />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/address" element={<Address />} />
        <Route path="/add-address" element={<AddAddress />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/order-details" element={<OrderDetails />} />
        <Route path="/rating-review" element={<RatingAndReview />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/support-tickets" element={<SupportTicket />} />
        <Route
          path="/create-support-ticket"
          element={<CreateSupportTicket />}
        />
        <Route path="/support-chat" element={<SupportChat />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/mobile-privacy-policy" element={<AppPrivacyPolicy />} />

        <Route path="/terms-and-conditions" element={<TermsAndCondition />} />
        <Route path="/return-policy" element={<ReturnPolicy />} />
        <Route path="/shipping-policy" element={<ShippingPolicy />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="*" element={<Navigate to="/" replace />} />

        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/otp-verification" element={<OTPVerification />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndCondition />} />
        <Route path="/return-policy" element={<ReturnPolicy />} />
        <Route path="/shipping-policy" element={<ShippingPolicy />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
    
      </>
    </Routes>
  );
};

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = await getToken();
        const userId = await getUserId();

        if (token) {
          dispatch(setIsLoggedIn(true));
          if (userId) {
            const reqData = {
              user_id: userId,
            };
            const response = await appApiHandler.getProfile(reqData);
            if (response?.status === 200) {
              dispatch(setUserData(response?.data));
            }
            const cartResponse = await appApiHandler.getCart(reqData);
            if (cartResponse?.status === 200 && cartResponse?.data?.products) {
              dispatch(setCartCount(cartResponse?.data?.products?.length));
            }
            const whishlistResponse = await appApiHandler.getWishlist(reqData);
            if (whishlistResponse?.status === 200) {
              dispatch(setWhishlist(whishlistResponse?.data || []));
              dispatch(setWhishlistCount(whishlistResponse?.data?.length));
            }
          }
        } else {
          dispatch(setIsLoggedIn(false));
        }
        const categories = await appApiHandler.getCategories();
        if (categories?.status === 200 && categories?.data) {
          const filteredCategories = categories.data.filter(
            (cat) => cat.active_product_count > 0,
          );
          dispatch(setCategories(filteredCategories));
        }
        const homeData = await appApiHandler.getHomeData({});
        if (homeData?.status === 200) {
          dispatch(
            setHomePromoData({
              promoTexts: homeData.promo_texts || [],
              announcementMarquees: homeData.announcement_marquees || [],
            }),
          );
        }
      } catch (error) {
        console.log("Error checking authentication:", error);
        dispatch(setIsLoggedIn(false));
      }
    };

    checkAuth();
  }, [dispatch]);

  return (
    <div className="app-shell">
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        {/* <TopBar />
       <HomeHeader /> */}
        <AppContent />
      </BrowserRouter>
      <ToastContainer />
    </div>
  );
}

export default App;
